import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CreateAssignmentInput, UpdateAssignmentInput } from '@/lib/marina/validators'

export type AssignmentStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'BLOCKED' | 'CANCELLED'
export type AssignmentPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'
export type AssignmentCategory = 'CUSTOMER_SUPPORT' | 'MAINTENANCE' | 'CLEANING' | 'ADMIN' | 'SECURITY' | 'OTHER'
export type AssignmentReferenceType = 'BOOKING' | 'CUSTOMER' | 'BOAT' | 'INVOICE' | 'CONTRACT' | 'OTHER'

export interface Assignment {
  id: string
  title: string
  description: string | null
  status: AssignmentStatus | null
  priority: AssignmentPriority | null
  category: AssignmentCategory | null
  startDate: string | null
  deadline: string | null
  assignedStaffId: string | null
  assignedStaff?: { id: string; name: string; email: string } | null
  referenceType: AssignmentReferenceType | null
  referenceId: string | null
  createdAt: string
  updatedAt: string
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'include', ...init })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(err.error || `HTTP ${res.status}`)
  }
  return res.json()
}

async function jsonFetch<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(err.error || `HTTP ${res.status}`)
  }
  return res.json()
}

export function useAssignments(
  params: {
    q?: string
    status?: AssignmentStatus
    priority?: AssignmentPriority
    category?: AssignmentCategory
    page?: number
    pageSize?: number
  } = {},
) {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) if (v) qs.set(k, String(v))
  return useQuery({
    queryKey: ['assignments', params],
    queryFn: () => apiFetch<{ items: Assignment[]; total: number }>(`/api/assignments${qs.toString() ? `?${qs}` : ''}`),
    staleTime: 15_000,
  })
}

export function useAssignment(id: string | null | undefined) {
  return useQuery({
    queryKey: ['assignment', id],
    queryFn: () => apiFetch<{ assignment: Assignment }>(`/api/assignments/${id}`),
    enabled: !!id,
  })
}

export function useCreateAssignment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateAssignmentInput) =>
      jsonFetch<{ assignment: Assignment }>('/api/assignments', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assignments'] }),
  })
}

export function useUpdateAssignment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAssignmentInput }) =>
      jsonFetch<{ assignment: Assignment }>(`/api/assignments/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: ['assignments'] })
      qc.invalidateQueries({ queryKey: ['assignment', v.id] })
    },
  })
}

export function useDeleteAssignment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => jsonFetch<{ ok: boolean }>(`/api/assignments/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assignments'] }),
  })
}
